import { LitElement, nothing } from 'lit';
import { customElement } from 'lit/decorators.js';

/**
 * @element kd-header-sticky
 * @description スティッキーヘッダーの表示/非表示をスクロール方向で制御する。
 * data-sticky-enabled="true" の場合のみ動作する。
 *
 * Smart App Banner（アプリ誘導バナー）は、本ヘッダーの最上部に in-flow で SSR 描画される
 * （snippets/v2-app-banner.liquid、layout/v2-theme.liquid の head スクリプトで表示条件を判定）。
 * バナーは本要素の子＝ヘッダー高さの一部なので、ResizeObserver がスペーサー／--v2-header-height を
 * 自動追従する。旧 smartbanner.js の position:absolute + <html> margin-top 方式（バナーがスクロールで
 * 流れ、fixed ヘッダーが毎スクロール追いかける janky な機構）は廃止し、本要素からもバナー追従ロジックを
 * 撤去した。dismiss はバナーの display 切替のみで、ヘッダー高さの変化として ResizeObserver が吸収する。
 */
@customElement('kd-header-sticky')
export class KdHeaderSticky extends LitElement {
  createRenderRoot() {
    return this;
  }

  /** 前回のスクロール位置 */
  private lastScrollY = 0;

  /** スクロールリスナーの参照 */
  private handleScroll: (() => void) | null = null;

  /** fixed用スペーサー要素 */
  private spacer: HTMLElement | null = null;

  /** グローバルナビ要素 */
  private nav: HTMLElement | null = null;

  /** スペーサー高さ／ナビ top 自動追従用 ResizeObserver（ヘッダー＋ナビの高さ変化に追従） */
  private resizeObserver: ResizeObserver | null = null;

  /**
   * ヘッダー＋ナビの合計高さ（＝スペーサー高さ／--v2-header-height）のキャッシュ。
   * updateLayout で更新し、scroll ハンドラは毎回の offsetHeight 参照（レイアウト強制）を避けて
   * 「最上部ガード」の判定にこの値を使う。
   */
  private headerBlockHeight = 0;

  /** スティッキー判定の閾値（px） */
  private static readonly SCROLL_THRESHOLD = 10;

  render() {
    return nothing;
  }

  connectedCallback(): void {
    super.connectedCallback();

    const enabled = this.dataset.stickyEnabled === 'true';

    this.style.display = 'block';

    if (!enabled) return;
    this.style.position = 'fixed';
    this.style.top = '0';
    this.style.left = '0';
    this.style.width = '100%';
    // ヘッダー内の dropdown（キーワードサジェスト等）が nav (z:10) の上に
    // 出られるよう、ヘッダー本体は nav より一段上の z:11 にする。
    this.style.zIndex = '11';
    this.style.transition = 'transform 200ms ease';

    // グローバルナビも fixed 化（top は updateLayout でヘッダー高さに合わせて静的に設定）
    this.nav = document.querySelector('[data-header-nav]');
    const navSection = this.nav?.closest('.shopify-section') as HTMLElement | null;
    if (navSection) {
      navSection.style.position = 'fixed';
      navSection.style.left = '0';
      navSection.style.width = '100%';
      navSection.style.zIndex = '10';
      navSection.style.transition = 'transform 200ms ease';
    }

    // fixed 解除分のスペーサーを挿入（ナビのセクションの後ろ）
    this.spacer = document.createElement('div');
    this.spacer.style.display = 'block';
    if (navSection) {
      navSection.after(this.spacer);
    } else {
      this.after(this.spacer);
    }

    // ResizeObserver でヘッダー／ナビ高さの変化（アプリバナーの表示/dismiss・帯の増減など）に追従し
    // スペーサー高さ・ナビ top・--v2-header-height を更新する。
    this.resizeObserver = new ResizeObserver(this.updateLayout);
    this.resizeObserver.observe(this);
    if (navSection) {
      this.resizeObserver.observe(navSection);
    }

    this.lastScrollY = window.scrollY;

    const show = (): void => {
      this.style.transform = 'translateY(0)';
      if (navSection) navSection.style.transform = 'translateY(0)';
    };
    const hide = (): void => {
      this.style.transform = 'translateY(-100%)';
      // ナビは自身の高さ＋ヘッダー高さぶん上へ退避（キャッシュ済みの合計高さを使用）
      if (navSection) navSection.style.transform = `translateY(-${this.headerBlockHeight}px)`;
    };

    this.handleScroll = () => {
      const currentY = window.scrollY;
      const diff = currentY - this.lastScrollY;

      if (currentY <= this.headerBlockHeight) {
        // 最上部ガード: 予約スペーサーが画面上部に見えている領域（0〜ヘッダー高さ）では、
        // スクロール方向に関わらず必ず表示する。これが無いと、最上部付近を小刻みに戻す
        // （上方向の各差分が SCROLL_THRESHOLD 未満）操作でヘッダーが非表示のまま固まり、
        // スペーサー分が空白として残る（TOP 上部の空白不具合）。
        show();
      } else if (diff > KdHeaderSticky.SCROLL_THRESHOLD) {
        // Scrolling down: hide
        hide();
      } else if (diff < -KdHeaderSticky.SCROLL_THRESHOLD) {
        // Scrolling up: show
        show();
      }

      this.lastScrollY = currentY;
    };

    window.addEventListener('scroll', this.handleScroll, { passive: true });

    // 初期レイアウト確定（アプリバナーが表示条件を満たし SSR で高さを持っているケースに備える）
    this.updateLayout();
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    if (this.handleScroll) {
      window.removeEventListener('scroll', this.handleScroll);
      this.handleScroll = null;
    }
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
    if (this.spacer) {
      this.spacer.remove();
      this.spacer = null;
    }
    document.documentElement.style.removeProperty('--v2-header-height');
  }

  /**
   * スペーサー高さ・ナビ top・--v2-header-height を、現在のヘッダー（＝アプリバナー込み）＋ナビ高さに同期する。
   * ResizeObserver から呼ばれ、バナーの表示/dismiss や帯の増減で高さが変わっても自動追従する。
   */
  private updateLayout = (): void => {
    if (!this.spacer) return;
    const navSection = this.nav?.closest('.shopify-section') as HTMLElement | null;
    const navH = navSection ? navSection.offsetHeight : 0;
    const total = this.offsetHeight + navH;
    // scroll ハンドラの最上部ガード／ナビ退避量が参照する合計高さをキャッシュ。
    this.headerBlockHeight = total;
    // ナビはヘッダー直下に固定（バナー込みのヘッダー高さぶん下げる）。
    if (navSection) navSection.style.top = `${this.offsetHeight}px`;
    this.spacer.style.height = `${total}px`;
    // var(--v2-header-height) を参照する sticky 要素（PDP 右カラム等）と SP ドロワー用に公開。
    document.documentElement.style.setProperty('--v2-header-height', `${total}px`);
  };
}
